import Failure from '../../../core/Failure';
import HttpClient from '../../../core/HttpClient';
import { Authentication } from '@leedpulse-monorepo/classes';
import { AccountType } from '@leedpulse-monorepo/enums';

/** Payload for new user path */
interface NewUserPayload {
  createNewUser: true;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
}

/** Payload for existing user path - userDoc required when createNewUser is false */
interface ExistingUserPayload {
  createNewUser: false;
  userDoc: { uid: string; email: string; phone: string } | null;
}

type UserPayload = NewUserPayload | ExistingUserPayload;

interface CompanyPayload {
  name: string;
  siret: string;
  notes: string;
}

interface PaymentPayload {
  accountType: AccountType;
  plan: string;
}

interface StripePayload {
  createNewCustomer: boolean;
  id: string;
}

export interface CreateCompanyFromBackofficePayload {
  user: UserPayload;
  company: CompanyPayload;
  payment: PaymentPayload;
  stripe: StripePayload;
}

export interface CreateCompanyFromBackofficeResponse {
  uid: string;
  company: {
    id: string;
    name: string;
    id_stripe: string;
    [key: string]: unknown;
  };
  link_stripe?: {
    created_subscriptions: string[];
    stripe_customer_id: string;
    old_stripe_customer_deleted: boolean;
  };
}

async function createCompanyFromBackoffice(
  payload: CreateCompanyFromBackofficePayload,
  auth: Authentication
): Promise<CreateCompanyFromBackofficeResponse> {
  try {
    let body: { user: Record<string, unknown> };

    if (payload.user.createNewUser) {
      body = {
        user: {
          createNewUser: true,
          firstname: payload.user.firstname,
          lastname: payload.user.lastname,
          email: payload.user.email,
          phone: payload.user.phone,
        },
      };
    } else {
      const doc = payload.user.userDoc;
      if (!doc) {
        throw new CreateCompanyFromBackofficeFailure('Utilisateur existant requis');
      }
      body = {
        user: {
          createNewUser: false,
          userDoc: doc,
        },
      };
    }

    const fullBody = {
      ...body,
      company: payload.company,
      payment: payload.payment,
      stripe: payload.stripe,
    };

    const response = await HttpClient.post(
      '/leedflow/company/create-from-backoffice',
      fullBody,
      auth.token
    );

    return response.data;
  } catch (e: unknown) {
    console.error('createCompanyFromBackoffice error:', e);
    const message = e instanceof Error ? e.message : 'Une erreur est survenue';
    throw new CreateCompanyFromBackofficeFailure(message);
  }
}

export class CreateCompanyFromBackofficeFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default createCompanyFromBackoffice;
